// eslint-disable-next-line @typescript-eslint/no-explicit-any
const remoteModules = new Map();
/**
 * Function to register a module to the remote modules registry.
 *
 * Call this function providing the module and a function that does a dynamic import of the module.
 *
 * @public
 * @remarks This is required to allow webpack to find all lazy loaded modules.
 * @param name - Name of the module.
 * @param loader - Async function that does a dynamic import of the module.
 */
export function registerRemoteModule(name, loader) {
    remoteModules.set(name, loader);
}
/**
 * In the unusual case the a module must get unregistered, this function can be used.
 * @public
 * @remarks This is very uncommon case and should be avoided.
 * @param name - Name of the module.
 */
export function unregisterRemoteModule(name) {
    remoteModules.delete(name);
}
const globalExtras = {};
/**
 * Function to provide a set of extras to any {@link LifecycleFactory}.
 * @public
 * @remarks - This is used by some frameworks like Angular to provide the Platform.
 *
 * 		- This will be injected in ALL {@link MFELoader} instances, if you need to avoid a conflict. Use local extra at the component level.
 * @param extras - Object with the extras, the name of the key has to match the expected by the {@link LifecycleFactory}.
 */
export function addGlobalExtras(extras) {
    Object.assign(globalExtras, extras);
}
var MFEAction;
(function (MFEAction) {
    MFEAction["Mount"] = "mount";
    MFEAction["Update"] = "update";
    MFEAction["Unmount"] = "unmount";
})(MFEAction || (MFEAction = {}));
const scriptErrorUI = (action, moduleName, error, scriptError) => {
    var _a, _b, _c;
    const showDetails = Boolean(localStorage.getItem('devtools'));
    if (!showDetails) {
        return `<h2>Sorry, there was an error loading this screen. Please reload the page.</h2>`;
    }
    const url = (_b = (_a = scriptError === null || scriptError === void 0 ? void 0 : scriptError.target) === null || _a === void 0 ? void 0 : _a.src) !== null && _b !== void 0 ? _b : '<url not detected>';
    const localRecommendation = `
		<h3>Recommended solutions:</h3>
		<ol>
			<li>
				If you see a <code>net::ERR_CERT_INVALID</code> error in the console,
				use the script link to open it on a new tab and accept the use of a self-signed certificate.
			</li>
			<li>
				If you see a <code>404</code> or <code>net::ERR_CONNECTION_REFUSED</code> related error,
				make sure that the URL is properly spelled and the development server it's running.
			</li>
			<li>Reload the page to allow the MFE to load in a clean state.</li>
		</ol>
	`;
    const loadingErrorText = `
		The script <a href="${url}" target="_blank">${url}</a> failed to load.<br>
	`;
    return `
		<style>
		div {
			font-size: 18px;
			padding: 50px;
			background-size: 56.57px 56.57px;
			background-image: linear-gradient(
				135deg,
				rgb(230 74 74 / 20%) 25%,
				rgb(247 206 143 / 20%) 25%,
				rgb(247 206 143 / 20%) 50%,
				rgb(230 74 74 / 20%) 50%,
				rgb(230 74 74 / 20%) 75%,
				rgb(247 206 143 / 20%) 75%,
				rgb(247 206 143 / 20%) 100%
			);
		}
		code {
			background-color: rgba(255, 2555, 2555, 0.6);
			padding: 3px 8px;
			font-weight: bold;
		}
		code.block {
			display: block;
			padding: 10px;
			font-weight: normal;
		}
		</style>
		<div>
			<h2>There was an error during the execution of the <code>${action}</code> step of the MFE <code>${moduleName}</code>.</h2>
			${scriptError ? loadingErrorText : ''}
			${localRecommendation}
			<h2>Error Stack</h2>
			<code class="block">
				${((_c = error.stack) !== null && _c !== void 0 ? _c : '').split('\n').join('<br>&nbsp;')}
			</code>
		</div>
	`;
};
/**
 * Removes all children from the shadowRoot of the node if has any
 * @param node - Node to remove children from
 */
function emptyShadowRoot(node) {
    if (node.shadowRoot) {
        while (node.shadowRoot.lastChild) {
            node.shadowRoot.removeChild(node.shadowRoot.lastChild);
        }
    }
}
/**
 * `mfe-loader` custom element class
 * @public
 */
export class MFELoader extends HTMLElement {
    constructor() {
        super(...arguments);
        /**
         * Object with properties that will be propagated to the MFE.
         * @remarks How propagated totally depend on the MFE and its technology.
         */
        this.props = {};
        /**
         * Defines if the MFE is unmounted or not.
         */
        this.unmounted = false;
        /**
         * Defined if the MFE had any errors during any of the lifecycle hooks.
         */
        this.hasError = false;
        /**
         * Internal object that helps to keep track of the actions being perform and avoid race conditions.
         */
        this.task = undefined;
        /**
         * Internal flag to use Shadow DOM as a wrapper.
         */
        this.useShadowDOM = false;
        this.internalLocalExtras = {};
        /**
         * Allows to prevent the unmount process in case that the node requires to be preserved
         */
        this.preventUnmount = false;
    }
    /**
     * List of attributes that will be listened for changes.
     */
    static get observedAttributes() {
        return ['module-name', 'props', 'shadow'];
    }
    /**
     * Local object that allows to provide Lifecycle Factory extras only to this MFE.
     * @remarks This is different from {@link addGlobalExtras} in that only apply to this MFE.
     */
    get localExtras() {
        return this.internalLocalExtras;
    }
    set localExtras(extras) {
        this.addLocalExtras(extras);
    }
    /**
     * Custom Element hook that is called when any of the attributes defined in {@link MFELoader.observedAttributes} changes.
     * @param name - Name of the attribute.
     * @param oldValue - Old value of the attribute.
     * @param newValue - New value of the attribute.
     */
    attributeChangedCallback(name, oldValue, newValue) {
        if (name === 'props' && newValue && oldValue !== newValue) {
            // Attributes are always string, so it needs to be parsed as JSON
            const parsedProps = JSON.parse(newValue);
            this.updateProperties(parsedProps);
        }
        if (name === 'module-name' && newValue && oldValue !== newValue) {
            this.mount(newValue);
        }
        if (name === 'shadow') {
            const newShadowDOMValue = typeof newValue === 'string';
            const shouldReMount = newShadowDOMValue !== this.useShadowDOM;
            this.useShadowDOM = newShadowDOMValue;
            if (shouldReMount && this.moduleName) {
                this.mount(this.moduleName);
            }
        }
    }
    /**
     * Custom Element hook that is called when the custom element gets added to the DOM.
     */
    connectedCallback() {
        this.style.display = 'block';
        if (!this.lifecycle && this.moduleName) {
            this.mount(this.moduleName);
        }
    }
    /**
     * Custom Element hook that is called when the custom element gets removed from the DOM.
     */
    disconnectedCallback() {
        if (this.preventUnmount) {
            return;
        }
        this.scheduleTask("unmount" /* Unmount */, async () => {
            if (this.lifecycle && !this.unmounted) {
                await this.unmount();
                this.props = undefined;
                this.moduleName = undefined;
                this.lifecycle = undefined;
                this.hasError = false;
                this.task = undefined;
                this.internalLocalExtras = {};
            }
        });
    }
    /**
     * Kicks the unmount process and waits for it if the component is not already unmounted
     */
    async unmount() {
        if (this.lifecycle && !this.unmounted) {
            await this.lifecycle.unmount();
            this.dispatchEvent(new CustomEvent('unmounted', { bubbles: true }));
            this.unmounted = true;
        }
    }
    /**
     * Allows to define Lifecycle Factory extras only for this MFE.
     * @remarks If the same extra name that is provided conflicts with a global one, the local one will win.
     * @param extras - Key-Value object with extras.
     */
    addLocalExtras(extras) {
        Object.assign(this.internalLocalExtras, extras);
    }
    async getLifecycleObject(moduleName, opts) {
        const remoteModuleLoader = remoteModules.get(moduleName);
        if (!remoteModuleLoader) {
            throw new Error(`No module is registered for '${moduleName}'. Make sure that registerRemoteModule was called with this module name`);
        }
        const { createLifecycle } = await remoteModuleLoader();
        return createLifecycle(opts);
    }
    /**
     * Load the module and mounts the MFE into the DOM by calling the Lifecycle mount hook
     * @param moduleName - Module name
     */
    mount(moduleName) {
        this.scheduleTask("mount" /* Mount */, async () => {
            const shouldReuseMFE = this.moduleName === moduleName && this.lifecycle && !this.unmounted && Boolean(this.lastMountingPoint);
            this.moduleName = moduleName;
            const totalGlobals = Object.assign(Object.assign({}, globalExtras), this.internalLocalExtras);
            const lifecycle = shouldReuseMFE && this.lifecycle ? this.lifecycle : await this.getLifecycleObject(this.moduleName, totalGlobals);
            const shouldUseShadowDOM = typeof lifecycle.shadow === 'boolean' ? lifecycle.shadow : this.useShadowDOM;
            if (shouldUseShadowDOM && !this.shadowRoot) {
                this.attachShadow({ mode: 'open' });
            }
            const mountPoint = shouldUseShadowDOM ? this.shadowRoot : this;
            if (shouldReuseMFE) {
                this.reParentShadowRoot(mountPoint);
            }
            else {
                await this.unmount();
                await lifecycle.bootstrap();
                await lifecycle.mount(() => { var _a; return (_a = this.lastMountingPoint) !== null && _a !== void 0 ? _a : mountPoint; }, this.props);
            }
            this.lifecycle = lifecycle;
            this.lastMountingPoint = mountPoint;
            this.dispatchEvent(new CustomEvent('mounted'));
            this.unmounted = false;
        });
    }
    /**
     * Move all the children to a new mounting point
     * @param mountPoint - New mounting point
     */
    reParentShadowRoot(mountPoint) {
        var _a, _b;
        const currentNodes = Array.from((_b = (_a = this.lastMountingPoint) === null || _a === void 0 ? void 0 : _a.childNodes) !== null && _b !== void 0 ? _b : []);
        emptyShadowRoot(this);
        if (!this.useShadowDOM && this.shadowRoot) {
            this.shadowRoot.append(document.createElement('slot'));
        }
        for (const node of currentNodes) {
            mountPoint.append(node);
        }
    }
    /**
     * Send updates of properties to the MFE.
     * @remarks How the properties will be propagated in the MFE depends on the MFE.
     * @param props - Object with properties.
     */
    updateProperties(props) {
        this.scheduleTask("update" /* Update */, async () => {
            var _a;
            this.props = props;
            if ((_a = this.lifecycle) === null || _a === void 0 ? void 0 : _a.update) {
                await this.lifecycle.update(this.props);
                this.dispatchEvent(new CustomEvent('updated'));
            }
        });
    }
    /**
     * Internal function to avoid race conditions on the internal operations
     * @param action - Action to perform
     * @param task - Function of the task to perform
     */
    scheduleTask(action, task) {
        var _a;
        if (this.hasError && action !== "unmount" /* Unmount */) {
            // In an error state, we don't do anything anymore except for unmounting
            return;
        }
        let scriptLoadError;
        const onGlobalError = (error) => {
            window.removeEventListener('error', onGlobalError);
            scriptLoadError = error;
        };
        window.addEventListener('error', onGlobalError, { capture: true, passive: true });
        this.task = ((_a = this.task) !== null && _a !== void 0 ? _a : Promise.resolve())
            .then(async () => {
            if (this.unmounted && action !== "unmount" /* Unmount */) {
                // Never do anything once the angular component unmounts
                return;
            }
            await task();
            window.removeEventListener('error', onGlobalError);
        })
            .catch((error) => {
            var _a;
            this.task = Promise.resolve();
            this.hasError = true;
            this.renderErrorUI(action, (_a = this.moduleName) !== null && _a !== void 0 ? _a : '<no module name>', error, scriptLoadError);
        });
    }
    renderErrorUI(action, moduleName, error, scriptError) {
        if (!this.shadowRoot) {
            this.attachShadow({ mode: 'open' });
        }
        this.shadowRoot.innerHTML = scriptErrorUI(action, moduleName, error, scriptError);
    }
}
/**
 * Register the {@link MFELoader} custom element as `mfe-loader` into the global scope.
 * @public
 */
export function registerMFELoader() {
    customElements.define('mfe-loader', MFELoader);
}
